/**
 * Installers, in Settings' Endpoints group: the read-only catalog of agent
 * installer artifacts.
 * Backed by:
 *   GET  /v1/installers              → list available installers
 *   POST /v1/installers:download-url → mint a pre-signed download URL
 *
 * Each row corresponds to one (platform, architecture, format,
 * version) combination. Clicking Download mints a fresh pre-signed
 * URL and opens it in a new tab; the URL expires after a short
 * window so we don't cache or persist it.
 *
 * Search, a count and the latest/all switch over a sticky-header table that
 * scrolls within its section. Nothing here creates anything.
 *
 * The table is built by hand in DataTable's look rather than with DataTable:
 * it is wider than the section beside Settings' menu, and Download stays
 * pinned to the visible right edge while it scrolls, which DataTable's
 * clipped cells can't do.
 */
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { restGet, restPost } from "../lib/restClient.js";
import OverflowTooltip from "../components/OverflowTooltip.jsx";
import { formatCompact } from "../lib/renderers.js";
import Button from "../components/ui/Button.jsx";
import Listing from "../components/ui/Listing.jsx";
import SearchInput from "../components/ui/SearchInput.jsx";
import { SkeletonRows } from "../components/ui/Skeleton.jsx";
import { Count } from "../components/ui/TableBar.jsx";
import Tabs from "../components/ui/Tabs.jsx";
import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";
import { compareSemver } from "../lib/semver.js";

// ── Schema + enums ───────────────────────────────────────────────

const COLUMNS = [
    { id: "platform",       label: "Platform",     width: 110 },
    { id: "architecture",   label: "Architecture", width: 110 },
    { id: "installerFormat",label: "Format",       width: 90 },
    { id: "version",        label: "Version",      width: 110 },
    { id: "createdAt",      label: "Published",    width: 130 },
    { id: "sizeBytes",      label: "Size",         width: 90 },
    { id: "downloadSha256", label: "SHA256",       width: 160 },
    { id: "_actions",       label: "",             width: 130 },
];

const PAGE_SIZE = 1000;

export const PLATFORM_LABEL = {
    PLATFORM_WINDOWS: "Windows",
    PLATFORM_MACOS:   "macOS",
    PLATFORM_LINUX:   "Linux",
    PLATFORM_UNKNOWN: "Unknown",
};

export const ARCH_LABEL = {
    PROCESSOR_ARCHITECTURE_X86:   "x86",
    PROCESSOR_ARCHITECTURE_X64:   "x64",
    PROCESSOR_ARCHITECTURE_ARM64: "ARM64",
    PROCESSOR_ARCHITECTURE_OTHER: "Other",
    PROCESSOR_ARCHITECTURE_UNKNOWN: "Unknown",
};

export const FORMAT_LABEL = {
    INSTALLER_FORMAT_MSI: "MSI",
    INSTALLER_FORMAT_EXE: "EXE",
    INSTALLER_FORMAT_PKG: "PKG",
};

// The table is wider than the section beside Settings' menu. Download stays
// pinned to the visible right edge, clear of the scroll bar, however far the
// table is scrolled. Its cell is opaque so columns pass under it; on hover it
// lays the row's hover tint over that, which in the dark theme is translucent.
const ACTIONS_HEADER = "sticky right-0 self-stretch content-center bg-[color:var(--bg)] pr-[14px]";
const ACTIONS_CELL = "sticky right-0 grid min-w-0 self-stretch content-center bg-[color:var(--panel)] pr-[14px] group-hover:[background-image:linear-gradient(var(--control-bg),var(--control-bg))]";
// DataTable's header and row look (components/DataTable.jsx).
const HEADER_ROW = "sticky top-0 z-10 grid items-center gap-x-2 border-b border-[color:var(--line-soft)] bg-[color:var(--bg)] px-[6px] py-[6px] text-[12px] font-medium leading-[1.2] text-[color:var(--muted)]";
const ROW = "group grid w-full items-center gap-x-2 border-t border-[color:var(--line-soft)] px-[6px] py-[5px] text-left hover:bg-[color:var(--control-bg)]";
const CELL = "truncate text-[12.5px] leading-[1.2]";
// First-load placeholder rows on the same grid as COLUMNS (keep in step).
const SKELETON_ROW = "grid h-[32px] grid-cols-[110px_110px_90px_110px_130px_90px_minmax(160px,1fr)_130px] items-center gap-x-2 px-[6px]";
const VIEWS = [{ id: "latest", label: "Latest" }, { id: "all", label: "All versions" }];
const SKELETON_CELLS = ["w-[70%]", "w-[50%]", "w-[40%]", "w-[60%]", "w-[60%]", "w-[50%]", "w-[80%]", "w-[60%]"];

// ── Row shape ────────────────────────────────────────────────────

export function flattenInstaller(i) {
    const created = i.createdAt ?? i.created_at ?? null;
    const createdDate = created ? new Date(created) : null;
    return {
        version:         i.version ?? "",
        os:              i.os ?? "",
        architecture:    i.architecture ?? "",
        installerFormat: i.installerFormat ?? i.installer_format ?? "",
        manualTesting:   i.manualTesting ?? i.manual_testing ?? false,
        downloadSha256:  i.downloadSha256  ?? i.download_sha256  ?? "",
        // proto declares uint64 — over JSON the gateway emits a string
        // for values that exceed the safe integer range. Keep a Number
        // for sub-PB sizes; fall back to NaN otherwise so the cell
        // renders the dash.
        sizeBytes:       Number(i.sizeBytes ?? i.size_bytes ?? 0),
        createdAt:       Number.isFinite(createdDate?.getTime?.()) ? createdDate : null,
    };
}

// ── Cells ────────────────────────────────────────────────────────

function formatBytes(n) {
    if (!Number.isFinite(n) || n <= 0) return null;
    const units = ["B", "KB", "MB", "GB", "TB"];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    const decimals = v >= 100 || i === 0 ? 0 : 1;
    return `${v.toFixed(decimals)} ${units[i]}`;
}

// Collapse a flat installer list to the single newest build per
// (platform, architecture, format) — so .msi and .exe for the same OS
// stay distinct rows. Ties on version fall back to the newer publish
// date. This is the default view; "Show all versions" bypasses it.
export function latestPerEnvironment(list) {
    const best = new Map();
    for (const r of list) {
        if (r.manualTesting) continue;
        const key = `${r.os}|${r.architecture}|${r.installerFormat}`;
        const cur = best.get(key);
        if (!cur) { best.set(key, r); continue; }
        const cmp = compareSemver(r.version, cur.version);
        if (cmp > 0) {
            best.set(key, r);
        } else if (cmp === 0) {
            const rt = r.createdAt?.getTime?.() ?? 0;
            const ct = cur.createdAt?.getTime?.() ?? 0;
            if (rt > ct) best.set(key, r);
        }
    }
    return Array.from(best.values());
}

// Collapse duplicate rows that are the SAME artifact published under more
// than one architecture key into a single row labeled "Universal". macOS
// ships a universal2 .pkg (arm64 + x86_64) that the release pipeline
// registers under both the macos/arm64 and macos/x64 keys pointing at one
// identical file — without this it would show as two near-identical rows.
// We only merge when the content hash matches across architectures, so a
// genuine per-arch artifact (or a transient version skew between arches)
// stays split. The first row's concrete architecture is kept as the
// download target; the bytes are identical regardless of which arch key
// we mint the URL for.
export function collapseUniversalRows(list) {
    const groups = new Map();
    const order = [];
    let solo = 0;
    for (const r of list) {
        const key = r.downloadSha256
            ? `${r.os}|${r.installerFormat}|${r.version}|${r.downloadSha256}`
            : `__solo_${solo++}`; // no hash → can't prove identity, never merge
        const g = groups.get(key);
        if (g) {
            g.archs.add(r.architecture);
        } else {
            groups.set(key, { row: r, archs: new Set([r.architecture]) });
            order.push(key);
        }
    }
    return order.map((k) => {
        const { row, archs } = groups.get(k);
        return { ...row, isUniversal: archs.size > 1 };
    });
}

// ── Page ─────────────────────────────────────────────────────────

export default function InstallersPage() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Bumped by Retry to re-run the fetch for the current page.
    const [attempt, setAttempt] = useState(0);
    const [search, setSearch] = useState("");
    const deferredSearch = useDeferredValue(search);
    // Default to the newest build per environment; toggle reveals every
    // published version (mirrors the dashboard's installers section).
    const [showAll, setShowAll] = useState(false);

    // Per-row download state. Map<rowKey, "pending" | error>.
    const [dlState, setDlState] = useState({});

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        restGet("/v1/installers", { page, page_size: PAGE_SIZE, include_manual_testing: true })
            .then((resp) => {
                if (cancelled) return;
                const list = (resp?.installers ?? []).map(flattenInstaller);
                setRows((prev) => (page === 0 ? list : [...prev, ...list]));
                setTotal(resp?.total ?? list.length);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("[installers]", err);
                setError(err.message || String(err));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [page, attempt]);

    const filteredRows = useMemo(() => {
        const q = deferredSearch.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            if (r.version?.toLowerCase().includes(q)) return true;
            if (PLATFORM_LABEL[r.os]?.toLowerCase().includes(q)) return true;
            if (ARCH_LABEL[r.architecture]?.toLowerCase().includes(q)) return true;
            if (FORMAT_LABEL[r.installerFormat]?.toLowerCase().includes(q)) return true;
            if (r.downloadSha256?.toLowerCase().includes(q)) return true;
            return false;
        });
    }, [rows, deferredSearch]);
    const isFiltering = deferredSearch.trim().length > 0;

    // Default view: newest build per (platform, arch, format), derived
    // from whatever's loaded + matched. "Show all versions" swaps in the
    // full list.
    const latestRows = useMemo(() => latestPerEnvironment(filteredRows), [filteredRows]);
    const displayRows = showAll ? filteredRows : latestRows;
    // Merge same-artifact-under-multiple-arches rows (macOS universal2) into
    // one "Universal" row. Runs after latest/show-all selection so it applies
    // to both views.
    const renderedRows = useMemo(() => collapseUniversalRows(displayRows), [displayRows]);

    const rowKey = useCallback(
        (r) => `${r.os}|${r.architecture}|${r.installerFormat}|${r.version}`,
        [],
    );

    // Click Download → POST to mint a pre-signed URL → open in a
    // new tab. The URL expires in a few minutes so we don't cache;
    // each click mints a fresh one.
    const handleDownload = useCallback(async (r) => {
        const key = rowKey(r);
        setDlState((m) => ({ ...m, [key]: "pending" }));
        try {
            const resp = await restPost("/v1/installers:download-url", {
                version: r.version,
                os: r.os,
                architecture: r.architecture,
                installer_format: r.installerFormat,
            });
            const url = resp?.downloadUrl ?? resp?.download_url ?? "";
            if (!url) throw new Error("Empty download_url in response");
            window.open(url, "_blank", "noopener,noreferrer");
            setDlState((m) => {
                const { [key]: _, ...rest } = m;
                return rest;
            });
        } catch (err) {
            console.error("[installers] download", err);
            setDlState((m) => ({ ...m, [key]: err.message || String(err) }));
        }
    }, [rowKey]);

    const gridCols = useMemo(
        // SHA256 takes the slack, so the table fills the page's width.
        () => COLUMNS.map((c) => (c.id === "downloadSha256" ? `minmax(${c.width}px,1fr)` : `${c.width}px`)).join(" "),
        [],
    );
    const minWidth = useMemo(() => COLUMNS.reduce((s, c) => s + c.width, 0), []);
    // Unknown until the first page lands, so the count holds its place
    // rather than reading "0".
    const count = total == null ? null : renderedRows.length;

    return (
        <Listing
            count={count}
            // A failed first read says so at the page's width; a failed Load
            // more sits beside the count over the rows already loaded.
            error={error ? (rows.length ? error : "Origin couldn't load the installer catalog. Try again in a moment.") : null}
            errorTitle="Installers unavailable"
            onRetry={() => setAttempt((n) => n + 1)}
            retrying={loading}
            // Something is published, so an empty view is the search's or the
            // Latest view's doing, and the bar stays to undo it.
            filtered={rows.length > 0}
            bar={<>
                <SearchInput
                    size="sm"
                    value={search}
                    onChange={setSearch}
                    label="Search installers"
                    placeholder="Platform, architecture, format, version, SHA…"
                    className="w-[300px] max-w-full"
                />
                <Count
                    n={count}
                    one={showAll ? (isFiltering ? "matching installer" : "installer") : "latest installer"}
                    many={showAll ? (isFiltering ? "matching installers" : "installers") : "latest installers"}
                />
                {total != null ? (
                    <span>
                        <span className="font-medium tabular-nums text-[color:var(--ink)]">{formatCompact(total)}</span> published
                    </span>
                ) : null}
            </>}
            barEnd={
                <Tabs
                    mode="toggle"
                    label="Versions"
                    items={VIEWS}
                    value={showAll ? "all" : "latest"}
                    onChange={(id) => setShowAll(id === "all")}
                />
            }
            empty={{ title: "No installers yet", note: "They appear here once an agent build is published." }}
            noMatch={isFiltering
                ? "No installers match this search. Clear it to see the full catalog."
                : "Only manual-testing builds are published. Select All versions to see them."}
        >
            <div className="min-w-0 overflow-x-auto">
                <div style={{ minWidth: `${minWidth}px`, width: "100%" }} aria-busy={loading ? "true" : undefined}>
                    <div className={HEADER_ROW} style={{ gridTemplateColumns: gridCols }}>
                        {COLUMNS.map((c) => (
                            <span key={c.id} className={c.id === "_actions" ? ACTIONS_HEADER : "truncate"}>{c.label}</span>
                        ))}
                    </div>

                    {count == null ? (
                        <SkeletonRows rows={4} cells={SKELETON_CELLS} rowClassName={SKELETON_ROW} label="Loading installers…" />
                    ) : (
                        <div style={{ opacity: loading ? 0.4 : 1, transition: "opacity 180ms ease" }}>
                            {renderedRows.map((r) => {
                                const key = rowKey(r);
                                const state = dlState[key];
                                const sizeStr = formatBytes(r.sizeBytes);
                                const shaShort = r.downloadSha256
                                    ? `${r.downloadSha256.slice(0, 12)}…`
                                    : "";
                                return (
                                    <div key={key} className={ROW} style={{ gridTemplateColumns: gridCols }}>
                                        <div className="grid min-w-0">
                                            <span className={`${CELL} text-[color:var(--ink)]`}>
                                                {PLATFORM_LABEL[r.os] ?? r.os ?? "—"}
                                            </span>
                                        </div>
                                        <div className="grid min-w-0">
                                            <span className={`${CELL} text-[color:var(--muted)]`}>
                                                {r.isUniversal ? "Universal" : (ARCH_LABEL[r.architecture] ?? r.architecture ?? "—")}
                                            </span>
                                        </div>
                                        <div className="grid min-w-0">
                                            <span className={`${CELL} text-[color:var(--muted)]`}>
                                                {FORMAT_LABEL[r.installerFormat] ?? r.installerFormat ?? "-"}
                                            </span>
                                            {r.manualTesting ? (
                                                <span className="truncate text-[11.5px] leading-[1.2] text-[color:var(--muted-soft)]">Manual testing only</span>
                                            ) : null}
                                        </div>
                                        <div className="grid min-w-0">
                                            <span className={`${CELL} tabular-nums text-[color:var(--ink)]`}>
                                                {r.version || "—"}
                                            </span>
                                        </div>
                                        <div className="grid min-w-0">
                                            {r.createdAt ? (
                                                <div className="grid gap-[1px]">
                                                    <span className="text-[12.5px] leading-[1.2] text-[color:var(--ink)]">
                                                        {timeLabelFormatter.format(r.createdAt)}
                                                    </span>
                                                    <span className="text-[11.5px] leading-[1.2] text-[color:var(--muted-soft)]">
                                                        {dayLabelFormatter.format(r.createdAt)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-[color:var(--muted-soft)]">—</span>
                                            )}
                                        </div>
                                        <div className="grid min-w-0">
                                            <span className={`${CELL} tabular-nums text-[color:var(--muted)]`}>
                                                {sizeStr ?? "—"}
                                            </span>
                                        </div>
                                        <div className="grid min-w-0">
                                            {shaShort ? (
                                                <OverflowTooltip
                                                    as="span"
                                                    text={r.downloadSha256}
                                                    className="font-mono text-[12px] leading-[1.2] text-[color:var(--muted)]"
                                                >
                                                    {shaShort}
                                                </OverflowTooltip>
                                            ) : (
                                                <span className="text-[color:var(--muted-soft)]">—</span>
                                            )}
                                        </div>
                                        <div className={ACTIONS_CELL}>
                                            {state === "pending" ? (
                                                <span className="text-[12px] text-[color:var(--muted)]">Generating link…</span>
                                            ) : typeof state === "string" ? (
                                                <span className="text-[12px] text-[color:var(--danger)]" title={state}>
                                                    Failed
                                                </span>
                                            ) : (
                                                <Button size="sm" className="justify-self-start" onClick={() => handleDownload(r)}>
                                                    Download
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {showAll && total != null && rows.length < total ? (
                        <div className="sticky left-0 grid place-items-center pb-[10px] pt-[8px]">
                            <Button size="sm" onClick={() => setPage((p) => p + 1)} disabled={loading}>
                                Load {formatCompact(total - rows.length)} more
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>
        </Listing>
    );
}
